import React, { useEffect } from 'react';

import { LineSegment2D } from '@paragon/geometry-library';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { handleLinePickedDuringPlanePicking } from '../../store/actions/layout/layoutPickingActions';
import { getPickingObjectInfo } from '../../store/selectors/pickingSelectors';
import {
  hoveredPlaneClearedDuringPlanePicking,
  hoveredPlaneSetDuringPlanePicking,
  modeSetForPlanePicking,
  STANDARD_PLANE_PICKING_MODE,
} from '../../store/slices/applicationSlice';
import { useMousePositionInScene } from '../hooks/useMousePositionInScene';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../util/events/types';
import { HoveredEntitySnapVisual } from './HoveredEntitySnapVisual';
import { layoutColors } from './layoutColors';
import { useStandardLineSnapper } from './LinePickingVisualsAndEventListeners';

export function PlanePickingEventListeners() {
  const dispatch = useAppDispatch();

  const snapToLine = useStandardLineSnapper();

  const mousePosition = useMousePositionInScene();
  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);

  const activeMode = pickingObjectInfo?.type === 'pickingPlane' ? pickingObjectInfo.activeMode : null;
  const shouldAllowPickingAbsoluteVerticalPlanes =
    pickingObjectInfo?.type === 'pickingPlane'
      ? pickingObjectInfo.targetInfo.type !== 'truss-envelope-top-cut' &&
        pickingObjectInfo.targetInfo.type !== 'truss-envelope-bottom-cut' &&
        pickingObjectInfo.targetInfo.type !== 'fascia-height-reference' &&
        pickingObjectInfo.targetInfo.type !== 'corner-set-plane-1' &&
        pickingObjectInfo.targetInfo.type !== 'corner-set-plane-2'
      : false;

  // Enter absolute vertical plane picking mode when shift is pressed.
  useEffect(() => {
    if (activeMode == null || activeMode === 'absoluteVertical' || !shouldAllowPickingAbsoluteVerticalPlanes) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Shift') {
        dispatch(modeSetForPlanePicking('absoluteVertical'));
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => void document.removeEventListener('keydown', onKeyDown);
  }, [activeMode, dispatch, shouldAllowPickingAbsoluteVerticalPlanes]);

  // Exit absolute vertical plane picking mode when shift is released.
  useEffect(() => {
    if (activeMode == null || activeMode !== 'absoluteVertical') return;

    function onKeyUp(event: KeyboardEvent) {
      if (event.key === 'Shift') {
        dispatch(modeSetForPlanePicking(STANDARD_PLANE_PICKING_MODE));
      }
    }

    document.addEventListener('keyup', onKeyUp);
    return () => void document.removeEventListener('keyup', onKeyUp);
  }, [activeMode, dispatch]);

  // Generate cut previews based on hovered lines when applicable.
  useEffect(() => {
    if (pickingObjectInfo?.type !== 'pickingPlane' || mousePosition == null) return;

    const staleHoveredPlane =
      pickingObjectInfo.hoveredPlane?.type === 'absoluteVertical' ? pickingObjectInfo.hoveredPlane.geometry : null;

    if (pickingObjectInfo.activeMode !== 'absoluteVertical') {
      if (staleHoveredPlane) {
        dispatch(hoveredPlaneClearedDuringPlanePicking());
      }

      return;
    }

    const previewSnap = snapToLine(mousePosition);

    if (previewSnap == null) {
      if (pickingObjectInfo.hoveredPlane?.type === 'absoluteVertical') {
        dispatch(hoveredPlaneClearedDuringPlanePicking());
      }

      return;
    }

    const previewSnapSegment = new LineSegment2D(...previewSnap.getDefiningPoints());

    if (staleHoveredPlane) {
      const staleHoveredSegment = LineSegment2D.fromLineSegmentObject(staleHoveredPlane.segment);
      if (previewSnapSegment.equals(staleHoveredSegment)) {
        return;
      }

      dispatch(
        hoveredPlaneSetDuringPlanePicking({
          type: 'absoluteVertical',
          geometry: { segment: previewSnapSegment.toObject(), isInfinite: !previewSnap.isSegment() },
        }),
      );
    } else {
      dispatch(
        hoveredPlaneSetDuringPlanePicking({
          type: 'absoluteVertical',
          geometry: { segment: previewSnapSegment.toObject(), isInfinite: !previewSnap.isSegment() },
        }),
      );
    }
  }, [mousePosition, pickingObjectInfo, snapToLine, dispatch]);

  // Pick line as absolute vertical plane when clicked.
  useEffect(() => {
    if (pickingObjectInfo?.type !== 'pickingPlane') return;

    function onSceneClick(event: GlobalThreeJSDomObjectEvent) {
      if (pickingObjectInfo?.type !== 'pickingPlane' || pickingObjectInfo.activeMode !== 'absoluteVertical') return;

      const pickedSnap = snapToLine(event.eventPosition.worldPosition);

      if (pickedSnap) {
        dispatch(handleLinePickedDuringPlanePicking(pickedSnap));
        return false; // stop propagation
      }
    }

    threeJsDom.addGlobalListener('click', onSceneClick);
    return () => void threeJsDom.removeGlobalListener('click', onSceneClick);
  }, [dispatch, pickingObjectInfo, snapToLine]);

  if (
    mousePosition &&
    pickingObjectInfo?.type === 'pickingPlane' &&
    pickingObjectInfo.activeMode === 'absoluteVertical'
  ) {
    const previewSnap = snapToLine(mousePosition);
    if (previewSnap) {
      return <HoveredEntitySnapVisual snap={previewSnap} color={layoutColors.entitySnap} />;
    }
  }

  return null;
}
